import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sun, Moon, LogIn, Eye, EyeOff } from "lucide-react";
import api from "../api/index";
import { useTheme } from "../context/ThemeContext";

export default function Login({ setCurrentUser }) {
  const { theme, isDark, toggleTheme } = useTheme();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const form = new FormData();
      form.append("username", username);
      form.append("password", password);
      await api.post("/auth/token", form);
      const me = await api.get("/auth/me");
      setCurrentUser(me.data);
      navigate("/chat");
    } catch {
      setError("Kullanıcı adı veya şifre hatalı.");
    } finally {
      setLoading(false);
    }
  };

  const t = theme;

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: t.bg,
        fontFamily: "'Inter',sans-serif",
        transition: "background 0.3s",
      }}
    >
      <div style={{ width: 360, position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: 28,
              fontWeight: 600,
              color: t.text,
              letterSpacing: 0.5,
            }}
          >
            TurnChat
          </div>
          <div
            style={{
              fontSize: 12,
              color: t.textMuted,
              marginTop: 5,
              letterSpacing: 0.3,
            }}
          >
            konuşma analizi platformu
          </div>
        </div>

        <div
          style={{
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: 16,
            padding: "32px 28px",
            position: "relative",
          }}
        >
          <button
            onClick={toggleTheme}
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              background: "transparent",
              border: `1px solid ${t.border}`,
              borderRadius: 9,
              color: t.textMuted,
              cursor: "pointer",
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>

          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 18 }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: t.textSecondary,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Kullanıcı Adı
              </label>
              <input
                style={{
                  background: t.input,
                  border: `1px solid ${t.inputBorder}`,
                  borderRadius: 9,
                  padding: "10px 13px",
                  color: t.text,
                  fontSize: 13,
                  outline: "none",
                  fontFamily: "inherit",
                  width: "100%",
                }}
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="kullanici_adi"
                autoComplete="username"
                required
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label
                style={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: t.textSecondary,
                  letterSpacing: 0.5,
                  textTransform: "uppercase",
                }}
              >
                Şifre
              </label>
              <div style={{ position: "relative" }}>
                <input
                  style={{
                    background: t.input,
                    border: `1px solid ${t.inputBorder}`,
                    borderRadius: 9,
                    padding: "10px 38px 10px 13px",
                    color: t.text,
                    fontSize: 13,
                    outline: "none",
                    fontFamily: "inherit",
                    width: "100%",
                  }}
                  type={showPass ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••"
                  autoComplete="current-password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    color: t.textMuted,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>

            {error && (
              <div
                style={{
                  background: t.error,
                  border: `1px solid ${t.errorBorder}`,
                  borderRadius: 8,
                  padding: "9px 12px",
                  color: t.errorText,
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                background: t.accent,
                border: "none",
                borderRadius: 9,
                padding: "11px",
                color: t.accentText,
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                opacity: loading ? 0.7 : 1,
                transition: "opacity 0.2s",
                marginTop: 2,
              }}
            >
              <LogIn size={14} />
              {loading ? "Giriş yapılıyor..." : "Giriş Yap"}
            </button>
          </form>

          <div
            style={{
              borderTop: `1px solid ${t.border}`,
              marginTop: 22,
              paddingTop: 18,
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 12, color: t.textMuted }}>
              Hesabın yok mu?{" "}
            </span>
            <Link
              to="/register"
              style={{
                fontSize: 12,
                color: t.accent,
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Kayıt ol
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
